import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import DicaPrimeiraVez from '@/components/feature/DicaPrimeiraVez';
import { basePathSite } from '@/pages/assistente-evento/utils';
import EstadoVendas from './components/EstadoVendas';
import ResumoVendas from './components/ResumoVendas';
import PainelBarracas from './components/PainelBarracas';
import PainelProdutos from './components/PainelProdutos';
import PainelOperadores from './components/PainelOperadores';
import CartaoTaxas from './components/CartaoTaxas';
import ModalBarraca, { type PontoEdicao } from './components/ModalBarraca';
import ModalProdutoCurto from './components/ModalProdutoCurto';
import ModalCardapio from './components/ModalCardapio';
import ModalConvidar from './components/ModalConvidar';
import BlocoCaixasMaquininhas from './components/maquininhas/BlocoCaixasMaquininhas';
import type { PainelLoja, PontoPainel, ProdutoPainel } from '@/pages/fichas/types';
import type { ProdutoLoja, ProdutoEditavel } from './types';

/** Cada pedaço da loja pode ser aberto sozinho a partir do hub "Dia do evento". */
export type SecaoLoja =
  | 'fichas'
  | 'caixa'
  | 'barracas'
  | 'produtos'
  | 'servindo'
  | 'maquininhas'
  | 'estado';

const TODAS: SecaoLoja[] = [
  'fichas',
  'caixa',
  'barracas',
  'produtos',
  'servindo',
  'maquininhas',
  'estado',
];

interface Props {
  eventoId: number;
  rotuloPonto?: string;
  rotuloPontos?: string;
  secoes?: SecaoLoja[];
}

interface Taxas {
  loja: string | null;
  pix: string | null;
  cartao: string | null;
  parcelado: string | null;
}

interface PontoExtra {
  descricao: string | null;
  imagem_url: string | null;
  ordem: number;
}

const CARD = 'rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';

function montarEditavel(
  p: ProdutoPainel,
  config: ProdutoLoja | undefined,
  custo: number | null,
): ProdutoEditavel {
  return {
    produto_id: p.produto_id,
    ponto_id: p.ponto_id,
    nome: p.nome,
    descricao: config?.descricao ?? null,
    categoria: config?.categoria ?? 'outro',
    emoji: p.emoji ?? config?.emoji ?? null,
    imagem_url: p.imagem_url ?? config?.imagem_url ?? null,
    imagem_slug: p.imagem_slug ?? config?.imagem_slug ?? null,
    preco_centavos: p.preco_centavos,
    custo_centavos: custo,
    estoque_total: p.estoque_total,
    so_para_inscritos: config?.so_para_inscritos ?? false,
    max_por_pedido: config?.max_por_pedido ?? null,
    venda_antecipada: p.venda_antecipada,
    abre_em: p.abre_em,
    fecha_em: p.fecha_em,
    alerta_restam: p.alerta_restam,
  };
}

// Conteúdo da loja do evento (barracas, produtos, quem serve, maquininhas, fichas
// e estado das vendas). A porta (pode_editar_evento) e o cabeçalho ficam no painel.
export default function ConteudoLoja({
  eventoId,
  rotuloPonto = 'Barraca',
  rotuloPontos = 'Barracas',
  secoes = TODAS,
}: Props) {
  const eventoIdNum = Number(eventoId);
  const [searchParams] = useSearchParams();
  const semCustoInicial = searchParams.get('sem_custo') === '1';

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [eventoNome, setEventoNome] = useState('');
  const [eventoSlug, setEventoSlug] = useState('');
  const [eventoInicio, setEventoInicio] = useState<string | null>(null);
  const [vendeProdutos, setVendeProdutos] = useState(false);
  const [taxas, setTaxas] = useState<Taxas>({
    loja: null,
    pix: null,
    cartao: null,
    parcelado: null,
  });

  const [painel, setPainel] = useState<PainelLoja | null>(null);
  const [produtosConfig, setProdutosConfig] = useState<ProdutoLoja[]>([]);
  const [custos, setCustos] = useState<Map<number, number | null>>(new Map());
  const [pontoExtras, setPontoExtras] = useState<Map<number, PontoExtra>>(new Map());

  const [modalBarraca, setModalBarraca] = useState<{ ponto: PontoEdicao | null } | null>(null);
  const [modalProduto, setModalProduto] = useState<{
    produto: ProdutoEditavel | null;
    pontoId: number | null;
  } | null>(null);
  const [cardapioPonto, setCardapioPonto] = useState<number | null>(null);
  const [convidarPonto, setConvidarPonto] = useState<number | null>(null);

  const [reload, setReload] = useState(0);
  const [copiadoFichas, setCopiadoFichas] = useState(false);
  const primeiraCarga = useRef(true);

  const mostra = (s: SecaoLoja) => secoes.includes(s);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      if (primeiraCarga.current) setCarregando(true);
      setErro(null);
      try {
        if (Number.isNaN(eventoIdNum)) {
          setCarregando(false);
          return;
        }

        const { data: eventoData, error: errEvento } = await supabase
          .from('eventos')
          .select(
            'nome, slug, vende_produtos, inicio_em, loja_quem_paga_taxa, quem_paga_taxa, quem_paga_taxa_cartao, quem_paga_taxa_parcelado',
          )
          .eq('id', eventoIdNum)
          .maybeSingle();

        if (errEvento) throw errEvento;
        if (!eventoData) {
          setCarregando(false);
          return;
        }

        const vende = eventoData.vende_produtos === true;

        if (ativo) {
          setEventoNome(eventoData.nome);
          setEventoSlug(eventoData.slug ?? '');
          setEventoInicio(eventoData.inicio_em ?? null);
          setVendeProdutos(vende);
          setTaxas({
            loja: eventoData.loja_quem_paga_taxa ?? null,
            pix: eventoData.quem_paga_taxa ?? null,
            cartao: eventoData.quem_paga_taxa_cartao ?? null,
            parcelado: eventoData.quem_paga_taxa_parcelado ?? null,
          });
        }

        if (vende) {
          const [resPainel, resProdutos, resCustos] = await Promise.all([
            supabase.rpc('loja_painel', { p_evento: eventoIdNum }),
            supabase.rpc('loja_produtos_do_evento', { p_evento: eventoIdNum }),
            supabase.from('loja_produtos').select('id, custo_centavos').eq('evento_id', eventoIdNum),
          ]);
          if (resPainel.error) throw resPainel.error;
          if (resProdutos.error) throw resProdutos.error;
          if (ativo) {
            setPainel((resPainel.data as PainelLoja) ?? null);
            setProdutosConfig((resProdutos.data as ProdutoLoja[]) || []);
            const mapa = new Map<number, number | null>();
            ((resCustos.data as { id: number; custo_centavos: number | null }[]) || []).forEach(
              (c) => mapa.set(c.id, c.custo_centavos),
            );
            setCustos(mapa);
          }
          try {
            const { data: pts } = await supabase
              .from('loja_pontos')
              .select('id, descricao, imagem_url, ordem')
              .eq('evento_id', eventoIdNum);
            const mapa = new Map<number, PontoExtra>();
            ((
              pts as { id: number; descricao: string | null; imagem_url: string | null; ordem: number }[]
            ) || []).forEach((pt) =>
              mapa.set(pt.id, {
                descricao: pt.descricao,
                imagem_url: pt.imagem_url,
                ordem: pt.ordem,
              }),
            );
            if (ativo) setPontoExtras(mapa);
          } catch {
            /* sem descrição/imagem */
          }
        } else if (ativo) {
          setPainel(null);
          setProdutosConfig([]);
          setPontoExtras(new Map());
        }

        if (ativo) setCarregando(false);
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e));
          setCarregando(false);
        }
      } finally {
        if (primeiraCarga.current) primeiraCarga.current = false;
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [eventoIdNum, reload]);

  // Tempo real.
  useEffect(() => {
    if (Number.isNaN(eventoIdNum) || !vendeProdutos) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const canal = supabase
      .channel(`loja-painel-${eventoIdNum}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'caixa_sinais',
          filter: `evento_id=eq.${eventoIdNum},origem=eq.vitrine`,
        },
        () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => setReload((r) => r + 1), 500);
        },
      )
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(canal);
    };
  }, [eventoIdNum, vendeProdutos]);

  function aoSalvo() {
    setReload((r) => r + 1);
  }

  const urlFichas = eventoSlug
    ? `${window.location.origin}${basePathSite()}/e/${eventoSlug}/fichas`
    : '';

  async function copiarLinkFichas() {
    if (!urlFichas) return;
    try {
      await navigator.clipboard.writeText(urlFichas);
    } catch {
      /* silencioso */
    }
    setCopiadoFichas(true);
    setTimeout(() => setCopiadoFichas(false), 2000);
  }

  function enviarLinkFichas() {
    if (!urlFichas) return;
    const texto = encodeURIComponent(`Compre suas fichas de ${eventoNome}: ${urlFichas}`);
    window.open(`https://wa.me/?text=${texto}`, '_blank', 'noopener,noreferrer');
  }

  function abrirEdicaoProduto(p: ProdutoPainel) {
    const config = produtosConfig.find((c) => c.produto_id === p.produto_id);
    const custo = custos.get(p.produto_id) ?? null;
    setModalProduto({ produto: montarEditavel(p, config, custo), pontoId: p.ponto_id });
  }

  function abrirEdicaoBarraca(p: PontoPainel) {
    const extra = pontoExtras.get(p.ponto_id);
    setModalBarraca({
      ponto: {
        ponto_id: p.ponto_id,
        nome: p.nome,
        descricao: extra?.descricao ?? null,
        imagem_url: extra?.imagem_url ?? null,
        ordem: extra?.ordem ?? 0,
      },
    });
  }

  // Nova barraca: o formulário abre direto.
  function novaBarraca() {
    setModalBarraca({ ponto: null });
  }

  if (carregando) {
    return (
      <div className="space-y-4">
        <div className="h-20 esqueleto rounded-3xl bg-secondary-100" />
        <div className="h-64 esqueleto rounded-3xl bg-secondary-100" />
      </div>
    );
  }

  if (erro) {
    return (
      <div className="rounded-3xl bg-white p-10 text-center">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={() => setReload((r) => r + 1)}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  if (!vendeProdutos) {
    return (
      <div className={CARD}>
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <i className="ri-store-2-line" />
          </span>
          <p className="text-[15px] font-bold text-foreground-950">Este evento vende produtos</p>
        </div>
        <p className="mt-1 text-[14px] text-foreground-500">
          A loja está desligada. Ligue em <strong className="font-semibold">Mais › Recursos ligados</strong>{' '}
          para criar barracas, produtos e chamar quem atende.
        </p>
        <Link
          to={`/gerenciar/${eventoIdNum}?aba=mais&tela=recursos`}
          className="mt-3 flex min-h-[44px] w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl bg-primary-500 px-4 text-[14px] font-bold text-white transition-colors hover:bg-primary-600"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-toggle-line" />
          </span>
          Abrir Recursos ligados
        </Link>
      </div>
    );
  }

  return (
    <div>
      {mostra('barracas') && <DicaPrimeiraVez id="painel.loja.primeira" className="mb-4" />}

      {mostra('fichas') && eventoSlug && (
        <div className={CARD}>
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <i className="ri-store-2-line" />
            </span>
            <p className="text-[15px] font-bold text-foreground-950">Link das fichas</p>
          </div>
          <p className="mt-1 text-[14px] text-foreground-500">
            Envie para quem quiser comprar fichas — abre sem login.
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <code className="min-w-0 truncate font-mono text-[13px] text-foreground-800">
              {urlFichas.replace(/^https?:\/\//, '')}
            </code>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Link
                to={`/cartaz-fichas/${eventoIdNum}`}
                className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-100 bg-white px-3 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-primary-50"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-printer-line" />
                </span>
                Cartaz
              </Link>
              <button
                type="button"
                onClick={enviarLinkFichas}
                className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-100 bg-white px-3 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-primary-50"
              >
                <span className="flex h-4 w-4 items-center justify-center text-success-600">
                  <i className="ri-whatsapp-line" />
                </span>
                WhatsApp
              </button>
              <button
                type="button"
                onClick={copiarLinkFichas}
                className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-3 text-[13px] font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={copiadoFichas ? 'ri-check-line' : 'ri-file-copy-line'} />
                </span>
                {copiadoFichas ? 'Copiado ✓' : 'Copiar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mostra('caixa') && (
        <div className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <i className="ri-cash-line" />
            </span>
            <p className="text-[15px] font-bold text-foreground-950">Caixa</p>
          </div>
          <p className="mt-1 text-[14px] text-foreground-500">
            Abra o caixa para vender e, no fim do turno, feche para conferir.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to="/caixa"
              className="flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-2xl bg-primary-500 px-4 text-[14px] font-bold text-white transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-cash-line" />
              </span>
              Abrir o caixa
            </Link>
            <Link
              to={`/caixa/fechamento?evento=${eventoIdNum}`}
              className="flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-100 bg-white px-4 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-primary-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-bar-chart-line" />
              </span>
              Fechar o caixa
            </Link>
            <Link
              to={`/gerenciar/${eventoIdNum}/loja/fichas-imprimir`}
              className="flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-100 bg-white px-4 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-primary-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-printer-line" />
              </span>
              Fichas para imprimir
            </Link>
          </div>
        </div>
      )}

      {mostra('barracas') && painel && (
        <div className="mt-4">
          <PainelBarracas
            pontos={painel.por_ponto}
            rotuloPonto={rotuloPonto}
            rotuloPontos={rotuloPontos}
            aoNova={novaBarraca}
            aoEditar={abrirEdicaoBarraca}
            aoConvidar={(p) => setConvidarPonto(p.ponto_id)}
            aoSalvo={aoSalvo}
          />
        </div>
      )}

      {mostra('produtos') && painel && (
        <div className={mostra('barracas') ? 'mt-4' : ''}>
          <PainelProdutos
            produtos={painel.por_produto}
            pontos={painel.por_ponto}
            custos={custos}
            semCustoInicial={semCustoInicial}
            rotuloPonto={rotuloPonto}
            aoCardapio={(pontoId) => setCardapioPonto(pontoId)}
            aoNovoProduto={(pontoId) => setModalProduto({ produto: null, pontoId })}
            aoNovaBarraca={novaBarraca}
            aoEditar={abrirEdicaoProduto}
            aoSalvo={aoSalvo}
          />
        </div>
      )}

      {mostra('servindo') && <PainelOperadores eventoId={eventoIdNum} aoSalvo={aoSalvo} />}

      {mostra('maquininhas') && <BlocoCaixasMaquininhas eventoId={eventoIdNum} />}

      {mostra('estado') && painel && (
        <div className="space-y-4">
          <EstadoVendas
            eventoId={eventoIdNum}
            painel={painel}
            eventoInicio={eventoInicio}
            aoMudou={aoSalvo}
          />
          <ResumoVendas painel={painel} rotuloPontos={rotuloPontos} />
          <CartaoTaxas
            eventoId={eventoIdNum}
            loja={taxas.loja}
            pix={taxas.pix}
            cartao={taxas.cartao}
            parcelado={taxas.parcelado}
            aoSalvo={aoSalvo}
          />
        </div>
      )}

      {modalBarraca && (
        <ModalBarraca
          eventoId={eventoIdNum}
          ponto={modalBarraca.ponto}
          aoFechar={() => setModalBarraca(null)}
          aoSalvo={aoSalvo}
        />
      )}

      {modalProduto && (
        <ModalProdutoCurto
          eventoId={eventoIdNum}
          pontos={painel?.por_ponto ?? []}
          produto={modalProduto.produto}
          pontoId={modalProduto.pontoId}
          aoFechar={() => setModalProduto(null)}
          aoSalvo={aoSalvo}
        />
      )}

      {cardapioPonto != null && (
        <ModalCardapio
          eventoId={eventoIdNum}
          pontoId={cardapioPonto}
          aoFechar={() => setCardapioPonto(null)}
          aoSalvo={aoSalvo}
        />
      )}

      {convidarPonto != null && (
        <ModalConvidar
          eventoId={eventoIdNum}
          pontoId={convidarPonto}
          aoFechar={() => setConvidarPonto(null)}
          aoSalvo={aoSalvo}
        />
      )}
    </div>
  );
}